/*
 * ManageTripsPanel — every currently active/watched trip on the Floor, with
 * an instant Archive (soft) and Delete (hard, admin-only, archived-first
 * safety net) action per row. Built for fast bulk-clearing of test/demo
 * clutter without going through Nova chat at all.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Trash2, AlertTriangle, RefreshCw, ShieldAlert } from "lucide-react";
import { apiClient } from "../../lib/api";

export default function ManageTripsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [scan, setScan] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/floor/trips/watched");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load watched trips");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const archive = async (tripId) => {
    setBusyId(tripId);
    try {
      const { data } = await apiClient.post(`/floor/trips/${tripId}/archive`);
      toast.success(`Archived · cleared ${data.cleared_alerts || 0} alert(s)`);
      setItems((prev) => prev.filter((t) => t.trip_id !== tripId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally { setBusyId(null); }
  };

  const del = async (tripId) => {
    setBusyId(tripId);
    try {
      await apiClient.delete(`/floor/trips/${tripId}`);
      toast.success("Trip permanently deleted");
      setItems((prev) => prev.filter((t) => t.trip_id !== tripId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed — archive it first");
    } finally { setBusyId(null); }
  };

  const runScan = async () => {
    setScanBusy(true);
    try {
      const { data } = await apiClient.get("/floor/admin/test-data-scan");
      setScan(data);
      const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
      if (!total) toast.success("No recognizable test/demo data found");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setScanBusy(false); }
  };

  const archiveAllScanned = async () => {
    if (!scan) return;
    setScanBusy(true);
    try {
      const { data } = await apiClient.post("/floor/admin/test-data-scan/archive", {
        trip_ids: scan.trips.map((t) => t.trip_id),
        intake_ids: scan.cpd_intakes.map((i) => i.id),
        template_ids: scan.recurring_templates.map((t) => t.template_id),
        tp_ids: scan.transportation_providers.map((t) => t.tp_id),
      });
      toast.success(`Archived ${data.archived_trips} trip(s), ${data.archived_intakes} intake(s), ${data.deactivated_templates} template(s), ${data.archived_tps} TP(s)`);
      setScan(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally { setScanBusy(false); }
  };

  return (
    <div data-testid="manage-trips-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate_navy">Manage Trips</h2>
          <p className="text-sm text-slate-500">Everything Nova is currently watching on the live Floor — archive or delete instantly.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={runScan} disabled={scanBusy}
                  className="inline-flex items-center gap-1 bg-amber-600 text-white px-3 py-2 rounded-md text-sm font-semibold disabled:opacity-60"
                  data-testid="manage-trips-scan-btn">
            <ShieldAlert size={14} /> Scan for test/demo data
          </button>
          <button onClick={load} disabled={loading}
                  className="inline-flex items-center gap-1 bg-forest text-white px-3 py-2 rounded-md text-sm font-semibold disabled:opacity-60"
                  data-testid="manage-trips-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {scan && <ScanResultBox scan={scan} busy={scanBusy} onConfirm={archiveAllScanned} onClose={() => setScan(null)} />}

      {loading && <div className="text-center text-slate-500 py-12" data-testid="manage-trips-loading">Loading watched trips…</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-16 text-slate-500 bg-white border border-dashed border-[#E2E0D8] rounded-xl" data-testid="manage-trips-empty">
          <div className="text-lg font-semibold mb-1">Nothing is being watched right now</div>
          <div className="text-sm">The live Floor is clear — no active trips need attention.</div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden">
          <div className="divide-y divide-[#F1EFE8]">
            {items.map((t) => (
              <div key={t.trip_id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm" data-testid={`manage-trips-row-${t.trip_id}`}>
                <div className="col-span-3 min-w-0">
                  <div className="font-semibold text-slate_navy truncate flex items-center gap-1">
                    {t.is_likely_test && <AlertTriangle size={12} className="text-amber-500 shrink-0" title="Looks like test/demo data" />}
                    {t.rider_name || "—"}
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono truncate" title={t.trip_id}>#{t.trip_id.slice(0, 12)}</div>
                </div>
                <div className="col-span-3 text-xs text-slate-600 truncate">{t.pickup_address || "—"}</div>
                <div className="col-span-2 text-xs text-slate-500">{t.status}</div>
                <div className="col-span-2 text-xs">
                  {t.has_open_alert
                    ? <span className="text-red-600 font-semibold" data-testid={`manage-trips-alert-flag-${t.trip_id}`}>⚠ open alert</span>
                    : <span className="text-slate-400">quiet</span>}
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button onClick={() => archive(t.trip_id)} disabled={busyId === t.trip_id}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline disabled:opacity-50"
                          data-testid={`manage-trips-archive-${t.trip_id}`}>
                    <Archive size={12} /> Archive
                  </button>
                  <button onClick={() => del(t.trip_id)} disabled={busyId === t.trip_id}
                          title="Archive first, then delete — safety net"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                          data-testid={`manage-trips-delete-${t.trip_id}`}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScanResultBox({ scan, busy, onConfirm, onClose }) {
  const total = Object.values(scan.counts || {}).reduce((a, b) => a + b, 0);
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6" data-testid="manage-trips-scan-result">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-slate_navy">
          Found {total} recognizable test/demo item(s)
        </div>
        <button onClick={onClose} className="text-xs text-slate-500 hover:underline" data-testid="manage-trips-scan-dismiss">dismiss</button>
      </div>
      <ul className="text-sm text-slate-700 mb-3 space-y-1">
        <li>Trips: {scan.counts.trips} {scan.trips.slice(0, 3).map((t) => t.rider_name).join(", ")}</li>
        <li>CPD intakes: {scan.counts.cpd_intakes} {scan.cpd_intakes.slice(0, 3).map((i) => i.officer_name).join(", ")}</li>
        <li>Recurring templates: {scan.counts.recurring_templates} {scan.recurring_templates.slice(0, 3).map((t) => t.rider_name).join(", ")}</li>
        <li>Transportation providers: {scan.counts.transportation_providers} {scan.transportation_providers.slice(0, 3).map((t) => t.name).join(", ")}</li>
      </ul>
      {total > 0 && (
        <button onClick={onConfirm} disabled={busy}
                className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-60"
                data-testid="manage-trips-scan-confirm">
          {busy ? "Archiving…" : `Archive all ${total} item(s)`}
        </button>
      )}
    </div>
  );
}
